/**
 * Mobile League Standings page — CD delivery `League Standings.dc.html` (children
 * `LeagueChrome` + `StandTable`). LeagueChrome (active `standings`) → **Season**
 * (date stepper) + **Current Week** (week-range subtitle) tables → BottomNav active
 * `leagues`. Reuses the locked StandTable widget EXACTLY for both tables — the
 * fit-to-width grid / two-row points+totals / category-leader colors / user-team gold
 * are all StandTable's. This screen adds only: the two-table layout, the canonical
 * section labels, the Season date stepper, and the Current-Week range subtitle.
 *
 * Spec compliance: flush / left-rule (NO rounded cards — the old mobileTileStyle card
 * is gone), section labels via the shared MobileSectionHeader (canonical 9px/9px,
 * 12px/600 — no local drifting header), hidden scrollbars + maxHeight 'none' (all
 * teams render flush, never scroll), tabular-nums, tokens only. The LeagueChrome nav
 * (active standings) + docked BottomNav are provided by the parent MobileLeagueHome /
 * MobileApp.
 */
import React, { useState, useEffect } from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { leaguesAPI } from '../../../services/apiService';
import StandingsDateStepper from '../components/StandingsDateStepper';
import StandTable from '../components/StandTable';
import MobileSectionHeader from '../components/MobileSectionHeader';

const C = dynastyTheme.tokens.colors;
const SECTION_BORDER = `1px solid ${C.border}`;
const NOTE_STYLE = { textAlign: 'center', padding: 24, color: C.muted, fontSize: 12 };

function MobileLeagueStandings({ leagueId, userTeam }) {
  const [standings, setStandings] = useState(null);
  const [currentWeekStandings, setCurrentWeekStandings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'totalPoints', direction: 'desc' });
  const [currentWeekSortConfig, setCurrentWeekSortConfig] = useState({ key: 'totalPoints', direction: 'desc' });
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [weekInfo, setWeekInfo] = useState(null);

  const userTeamId = userTeam?.team_id || userTeam?.user_id;

  useEffect(() => {
    if (leagueId) fetchStandings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  const formatStandings = (response) => ({
    categories: response.categories,
    teams: (response.standings ?? []).map(team => ({
      ...team,
      stats: team.stats,
      totalPoints: team.total_points ?? team.totalPoints ?? 0,
      category_points: team.category_points,
    })),
  });

  const fetchStandings = async () => {
    try {
      setLoading(true);
      // Parallel: season standings + weekly standings + available dates
      const [seasonRes, weekRes, datesRes] = await Promise.all([
        leaguesAPI.getLeagueStandings(leagueId),
        leaguesAPI.getLeagueStandings(leagueId, 'week'),
        leaguesAPI.getStandingsAvailableDates(leagueId),
      ]);

      if (seasonRes.success && seasonRes.standings) {
        setStandings(formatStandings(seasonRes));
        if (seasonRes.through_date) {
          setSelectedDate(seasonRes.through_date);
        }
      }
      if (weekRes.success && weekRes.standings) {
        setCurrentWeekStandings(formatStandings(weekRes));
        setWeekInfo({ week_start: weekRes.week_start, week_end: weekRes.week_end, through_date: weekRes.through_date });
      }
      if (datesRes.success && datesRes.dates) {
        setAvailableDates(datesRes.dates);
      }
    } catch (error) {
      console.error('Error fetching standings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = async (newDate) => {
    setSelectedDate(newDate);
    try {
      const res = await leaguesAPI.getLeagueStandings(leagueId, 'season', newDate);
      if (res.success && res.standings) {
        setStandings(formatStandings(res));
      }
    } catch (err) {
      console.error('Error fetching historical standings:', err);
    }
  };

  // Separate sort config per table.
  const handleSort = (key, isWeek = false) => {
    const setter = isWeek ? setCurrentWeekSortConfig : setSortConfig;
    setter(prev => ({ key, direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc' }));
  };

  if (loading) return <div style={NOTE_STYLE}>Loading standings…</div>;

  const weekRange = weekInfo
    ? `${new Date(weekInfo.week_start + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(weekInfo.week_end + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    : null;

  // tabular-nums on the whole screen → every StandTable number aligns (inherited; the
  // locked StandTable is untouched). Header is rendered by the parent MobileLeagueHome.
  return (
    <div style={{ paddingTop: 2, fontVariantNumeric: 'tabular-nums' }}>
      {/* ===== Season (date stepper in the header's right slot) ===== */}
      <div style={{ padding: '0 14px' }}>
        <MobileSectionHeader
          title="Season"
          right={(availableDates.length > 0 && selectedDate)
            ? <StandingsDateStepper currentDate={selectedDate} availableDates={availableDates} onDateChange={handleDateChange} />
            : null}
        />
      </div>
      {(standings && standings.teams && standings.teams.length > 0)
        ? <StandTable data={standings} sortConfig={sortConfig} onSort={(key) => handleSort(key, false)} userTeamId={userTeamId} userTeamPrimary={userTeam?.team_colors?.primary} leagueId={leagueId} maxHeight="none" />
        : <div style={NOTE_STYLE}>No standings yet</div>}

      {/* ===== Current Week (range subtitle) ===== */}
      <div style={{ padding: '0 14px', marginTop: 6, borderTop: SECTION_BORDER }}>
        <MobileSectionHeader
          title="Current Week"
          right={weekRange ? <span style={{ fontSize: 10, color: C.muted, whiteSpace: 'nowrap' }}>{weekRange}</span> : null}
        />
      </div>
      {(currentWeekStandings && currentWeekStandings.teams && currentWeekStandings.teams.length > 0)
        ? <StandTable data={currentWeekStandings} sortConfig={currentWeekSortConfig} onSort={(key) => handleSort(key, true)} userTeamId={userTeamId} userTeamPrimary={userTeam?.team_colors?.primary} leagueId={leagueId} maxHeight="none" />
        : <div style={NOTE_STYLE}>No current-week standings yet</div>}
    </div>
  );
}

export default MobileLeagueStandings;
